import { useEffect } from 'react'
import { AlertErrorCircle } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { Modal, ModalClose, ModalContent } from '@/components/modal'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { UserFilesBatchingActions, UserFilesBatchedOperationKind, UserFilesBatchedOperationStatus } from '@/user_files/batching/store/slice'
import { selectActiveUserFilesOperation } from '@/user_files/batching/store/selectors'
import { selectFolderDataById } from '@/user_files/folders/store/selectors'
import { selectActiveMatter } from '@/matter/store/selectors'
import { useDelayedVisibility } from '@/hooks/useDelayedVisibility'
import { matterPaths } from '@/routing/paths'

export function UserFilesOperationOverlay() {
  const dispatch = useAppDispatch()
  const activeOperation = useAppSelector(selectActiveUserFilesOperation)

  const isRunning = activeOperation?.status === UserFilesBatchedOperationStatus.Queued || activeOperation?.status === UserFilesBatchedOperationStatus.Running
  const isFailed = activeOperation?.status === UserFilesBatchedOperationStatus.Failed

  // Delay showing the overlay so fast operations (<500ms) skip it entirely and just show the success toast
  // Once shown, hold it for a minimum duration to avoid a jarring flash
  const showInProgress = useDelayedVisibility(Boolean(isRunning))
  const isSucceeded = activeOperation?.status === UserFilesBatchedOperationStatus.Succeeded

  const totalItems = activeOperation?.totalItems
  const operationKind = activeOperation?.kind
  const destinationFolderId = activeOperation?.destinationFolderId
  const destinationFolder = useAppSelector((state) => selectFolderDataById(state, { folderId: destinationFolderId }))
  const activeMatter = useAppSelector(selectActiveMatter)

  // Success toasts live here (not in the listener) so they fire once the overlay dismisses
  useEffect(() => {
    if (!isSucceeded || showInProgress) return

    const isMove = operationKind === UserFilesBatchedOperationKind.MoveSelection
    const itemCountText = totalItems === 1 ? '1 item' : `${totalItems} items`

    const toastText = isMove ? `${itemCountText} moved${destinationFolder ? ' to' : ''}` : `${itemCountText} deleted`

    const folderLink =
      isMove && destinationFolder
        ? destinationFolder.is_root
          ? { label: activeMatter?.name ?? 'Files', href: matterPaths.userFiles.root(destinationFolder.matter_id), inline: true }
          : { label: destinationFolder.name, href: matterPaths.userFiles.folder(destinationFolder.matter_id, destinationFolder.id), inline: true }
        : undefined

    dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: toastText, durationMs: kToastDurationShortMs, withLink: folderLink }))
    dispatch(UserFilesBatchingActions.clearActiveOperation())
  }, [showInProgress, isSucceeded, totalItems, operationKind, destinationFolder, activeMatter, dispatch])

  const open = showInProgress || isFailed

  if (!activeOperation || !open) return null

  const title = isFailed
    ? activeOperation.kind === UserFilesBatchedOperationKind.MoveSelection
      ? 'Move failed'
      : 'Delete failed'
    : activeOperation.kind === UserFilesBatchedOperationKind.MoveSelection
    ? 'Moving items…'
    : 'Deleting items…'
  const subtitleParts =
    activeOperation.totalItems > 1 ? [`${Math.min(activeOperation.processedItems, activeOperation.totalItems)}/${activeOperation.totalItems}`] : []
  if (isFailed && activeOperation.processedItems > 0) subtitleParts.push('Some items may have been processed.')
  const subtitle = subtitleParts.length > 0 ? subtitleParts.join(' • ') : undefined

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return
    if (showInProgress) return
    dispatch(UserFilesBatchingActions.clearActiveOperation())
  }

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalContent title={title} subtitle={subtitle} titleAlign="center" width="sm" height="max-sm" preventDismiss={showInProgress} showCloseButton={isFailed}>
        {showInProgress ? (
          <div className="flex items-center justify-center gap-3 py-4">
            <CircularProgressContinuousSized size={18} thickness={6} />
            <p className="text-sm text-brand-neutral-700">Please wait…</p>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <p className="p-2 flex items-center gap-1 text-xs text-red-800 rounded bg-red-50">
              <AlertErrorCircle className="shrink-0 w-3.5 h-3.5" />
              {activeOperation.errorMessage || 'Operation failed. Please refresh and try again.'}
            </p>
            <div className="flex justify-end">
              <ModalClose asChild>
                <Button variant="filled" color="primary">
                  Close
                </Button>
              </ModalClose>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  )
}
